import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, X, Home, Kanban, Lightbulb, Sparkles, BarChart3 } from "lucide-react";
import { client } from "@/lib/api";

const STEPS = [
  {
    id: "dashboard",
    eyebrow: "i. Your studio",
    title: "This is your",
    titleItalic: "morning ritual.",
    body: "Every time you open PintaFlow, you land here — today's tasks, your streak, and ideas in motion. One screen, zero noise.",
    icon: Home,
    route: "/dashboard",
    cta: "Show me the pipeline",
  },
  {
    id: "pipeline",
    eyebrow: "ii. The Pipeline",
    title: "Six rooms.",
    titleItalic: "One craft.",
    body: "Every idea moves through Idea → Research → Script → Create → Schedule → Published. Drag a card to move it. The drag itself is the ritual.",
    icon: Kanban,
    route: "/pipeline",
    cta: "Open the Vault",
  },
  {
    id: "vault",
    eyebrow: "iii. The Vault",
    title: "Capture every",
    titleItalic: "spark.",
    body: "Loose ideas, half-thoughts, late-night sparks — they all live in your Vault. Tag them, search them, promote them into the Pipeline when they're ready.",
    icon: Lightbulb,
    route: "/vault",
    cta: "Show the AI writer",
  },
  {
    id: "ai",
    eyebrow: "iv. The Co-pilot",
    title: "An AI writer with",
    titleItalic: "taste.",
    body: "On any idea, click \"Write script\" — PintaFlow's AI drafts a hook, intro, body and CTA tuned to your platform and tone. Always opinionated, never generic.",
    icon: Sparkles,
    route: "/pipeline",
    cta: "Show analytics",
  },
  {
    id: "analytics",
    eyebrow: "v. The mirror",
    title: "Numbers tell a",
    titleItalic: "story.",
    body: "Log views, likes and subs per post. PintaFlow weaves them into trends so you can see what's actually landing — and what to do more of.",
    icon: BarChart3,
    route: "/analytics",
    cta: "Start creating",
  },
];

export default function ProductTour({ onClose }) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const sealed = useRef(false);

  const current = STEPS[step];

  useEffect(() => {
    if (current?.route && window.location.pathname !== current.route) {
      navigate(current.route);
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const finish = async () => {
    if (sealed.current) return;
    sealed.current = true;
    try {
      await client.patch("/auth/tour", { seen: true });
    } catch {
      // ignore — purely cosmetic flag
    }
    onClose?.();
  };

  const next = () => {
    if (step < STEPS.length - 1) setStep((s) => s + 1);
    else finish();
  };

  const Icon = current.icon;

  return (
    <div
      className="fixed inset-0 z-[95] flex items-end md:items-center md:justify-end pointer-events-none"
      data-testid="product-tour"
    >
      {/* Soft dim, click-through on top half */}
      <div
        className="absolute inset-0 pointer-events-auto"
        style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)" }}
        onClick={finish}
      />

      <div
        className="relative pointer-events-auto m-4 md:m-10 w-full md:max-w-md rounded-3xl overflow-hidden"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--ink-22)",
          boxShadow: "0 40px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(216,168,87,0.08)",
        }}
      >
        {/* Top brass strip + step dots */}
        <div className="px-7 pt-6 pb-2">
          <div className="flex items-center justify-between">
            <div className="eyebrow">
              <span className="seal-dot mr-2 align-middle" /> Tour · {step + 1} / {STEPS.length}
            </div>
            <button onClick={finish} className="btn-ghost" data-testid="tour-close">
              <X size={16} />
            </button>
          </div>
          <div className="flex gap-1.5 mt-3">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className="h-1 rounded-full flex-1 transition"
                style={{ background: i <= step ? "var(--seal)" : "var(--ink-22)" }}
              />
            ))}
          </div>
        </div>

        <div className="px-7 pt-5 pb-2 flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ background: "rgba(216,168,87,0.10)", border: "1px solid rgba(216,168,87,0.40)" }}
          >
            <Icon size={16} style={{ color: "var(--seal)" }} strokeWidth={1.6} />
          </div>
          <div className="eyebrow">{current.eyebrow}</div>
        </div>

        <div className="px-7 pt-3 pb-5">
          <h2
            className="font-heading text-3xl md:text-4xl leading-[1.05]"
            style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}
          >
            {current.title} <span className="font-display-italic">{current.titleItalic}</span>
          </h2>
          <p className="text-sm mt-4 leading-relaxed" style={{ color: "var(--ink-72)" }}>
            {current.body}
          </p>
        </div>

        <div className="px-7 py-5 flex items-center justify-between gap-2" style={{ borderTop: "1px solid var(--ink-22)" }}>
          <button onClick={finish} className="btn-ghost text-xs" data-testid="tour-skip">
            Skip tour
          </button>
          <button onClick={next} className="btn-primary" data-testid="tour-next">
            {current.cta} <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
